/**
 * Cloudflare Image Resizing helper for R2 gallery images.
 *
 * Builds URLs in the form:
 *   /cdn-cgi/image/<options>/<R2_URL>
 *
 * Requires Image Resizing and "Resize from any origin" enabled in the
 * Cloudflare dashboard (Speed → Optimization → Image Resizing).
 *
 * Image Resizing is on by default in production. Set
 * NEXT_PUBLIC_USE_CLOUDFLARE_IMAGE_RESIZING=false at build time to use raw R2
 * URLs (for an emergency rollback or a local production preview).
 *
 * PERF-1 (2026-07-06): the flag is `NEXT_PUBLIC_`-prefixed on purpose. This
 * helper runs on BOTH the server (SSR / layouts) and the client (e.g. the
 * gallery grid via getOptimizedImageUrl). A non-public `process.env.*` inlines
 * to `undefined` in the client bundle, so a non-public flag would make the
 * server emit `/cdn-cgi/image/...` URLs while the client returned raw ones —
 * a hydration mismatch the moment the owner enabled it. `NEXT_PUBLIC_*` is
 * inlined identically into both bundles at build time, so server and client
 * always agree. (Consequence: it must be set as a BUILD variable, not just a
 * runtime one.)
 */

export interface ImageOptimizeOptions {
  width?: number;
  height?: number;
  fit?: "scale-down" | "contain" | "cover" | "crop" | "pad";
  format?: "auto" | "webp" | "avif" | "jpeg";
  quality?: number;
}

const DEFAULT_OPTIONS: ImageOptimizeOptions = {
  format: "auto",
  quality: 80,
};

function isImageResizingEnabled(): boolean {
  // NEXT_PUBLIC_ so the value is identical on server and client (see file
  // header) — this prevents a hydration mismatch. Default on; only the literal
  // "false" opts both server and client out.
  return process.env.NEXT_PUBLIC_USE_CLOUDFLARE_IMAGE_RESIZING !== "false";
}

/**
 * Build a Cloudflare Image Resizing URL for the given image URL.
 * Returns raw URL in development or when Image Resizing is explicitly disabled.
 */
export function getOptimizedImageUrl(
  imageUrl: string,
  options: ImageOptimizeOptions = {}
): string {
  if (!imageUrl || typeof imageUrl !== "string") return imageUrl;

  const isDev = process.env.NODE_ENV === "development";
  const useOptimization = isImageResizingEnabled();

  if (isDev || !useOptimization) return imageUrl;

  const opts = { ...DEFAULT_OPTIONS, ...options };
  const parts: string[] = [];

  if (opts.width != null) parts.push(`width=${opts.width}`);
  if (opts.height != null) parts.push(`height=${opts.height}`);
  if (opts.fit) parts.push(`fit=${opts.fit}`);
  if (opts.format) parts.push(`format=${opts.format}`);
  if (opts.quality != null) parts.push(`quality=${opts.quality}`);

  if (parts.length === 0) return imageUrl;

  const optionsStr = parts.join(",");
  // Keep the transformation same-origin. An absolute production URL is
  // blocked by localhost preview CSP and makes gallery hydration fall back to
  // the full-size R2 original; the relative path works on both preview and
  // production because Cloudflare handles it on the current zone.
  return `/cdn-cgi/image/${optionsStr}/${imageUrl}`;
}
